import React, { useState } from 'react';
import {
  TagIcon, PlusIcon, TrashIcon, PencilSquareIcon,
  PhotoIcon, SparklesIcon, CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { adminAPI, bannersAPI } from '../../services/api';
import {
  Card, SectionHeader, SearchInput, Button, Badge, StatusBadge,
  Modal, Input, Select, Textarea, Pagination, LoadingPage, EmptyState,
  ConfirmDialog, Toggle
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function MarketingAndPromotions() {
  const [activeTab, setActiveTab] = useState('coupons');
  const { page, setPage } = usePagination();

  // ── Coupons Query & Mutations ───────────────────────
  const { data: couponData, loading: couponLoading, refetch: refetchCoupons } = useQuery(
    () => adminAPI.couponsList({ page, limit: 15 }),
    [page, activeTab],
    { enabled: activeTab === 'coupons' }
  );

  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const { register: regCoupon, handleSubmit: handleCouponSubmit, reset: resetCoupon, setValue: setCouponVal } = useForm();

  const openNewCoupon = () => {
    setEditingCoupon(null);
    resetCoupon({ code: '', discount_type: 'percentage', discount_value: 10, min_order_value: 500, max_discount: 200, valid_until: '' });
    setCouponModalOpen(true);
  };

  const openEditCoupon = (c) => {
    setEditingCoupon(c);
    resetCoupon({
      code: c.code,
      discount_type: c.type || c.discount_type || 'percentage',
      discount_value: c.value || c.discount_value,
      min_order_value: c.min_order_value || 0,
      max_discount: c.max_discount || 0,
      valid_until: c.valid_until ? c.valid_until.substring(0, 10) : ''
    });
    setCouponModalOpen(true);
  };

  const onSaveCoupon = async (formVals) => {
    try {
      const payload = {
        code: formVals.code,
        type: formVals.discount_type === 'flat' ? 'fixed' : (formVals.discount_type || 'percentage'),
        value: Number(formVals.discount_value),
        min_order_value: Number(formVals.min_order_value) || 0,
        max_discount: formVals.max_discount ? Number(formVals.max_discount) : null,
        valid_from: new Date().toISOString(),
        valid_until: formVals.valid_until ? new Date(formVals.valid_until).toISOString() : new Date(Date.now() + 365*24*60*60*1000).toISOString(),
        is_active: 1
      };
      if (editingCoupon) {
        await adminAPI.updateCoupon(editingCoupon.id, payload);
        toast.success('Coupon updated successfully');
      } else {
        await adminAPI.createCoupon(payload);
        toast.success('Coupon created successfully');
      }
      setCouponModalOpen(false);
      refetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.message || 'Failed to save coupon');
    }
  };

  const onDeleteCoupon = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await adminAPI.deleteCoupon(id);
      toast.success('Coupon deleted');
      refetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.message || 'Failed to delete coupon');
    }
  };

  // ── Banners Query ───────────────────────────────────
  const { data: bannerData, loading: bannerLoading, refetch: refetchBanners } = useQuery(
    () => bannersAPI.adminGetAll({ page, limit: 15 }),
    [page, activeTab],
    { enabled: activeTab === 'banners' }
  );

  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const { register: regBanner, handleSubmit: handleBannerSubmit, reset: resetBanner } = useForm();

  const openNewBanner = () => {
    setEditingBanner(null);
    resetBanner({ title: '', image_url: '', target_url: '', position: 'home_hero', is_active: true });
    setBannerModalOpen(true);
  };

  const onSaveBanner = async (formVals) => {
    try {
      if (editingBanner) {
        await bannersAPI.update(editingBanner.id, formVals);
        toast.success('Banner updated');
      } else {
        await bannersAPI.create(formVals);
        toast.success('Banner created');
      }
      setBannerModalOpen(false);
      refetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.message || 'Failed to save banner');
    }
  };

  const onToggleBanner = async (b) => {
    try {
      await bannersAPI.toggleActive(b.id, { is_active: !b.is_active });
      toast.success(`Banner ${!b.is_active ? 'activated' : 'deactivated'}`);
      refetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Failed to toggle banner');
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <SectionHeader
        title="Marketing, Campaigns & Banners"
        subtitle="Manage platform-wide promo coupons, discount rules, hero carousels and promotional content"
        action={
          <Button
            variant="primary"
            size="sm"
            icon={PlusIcon}
            onClick={activeTab === 'coupons' ? openNewCoupon : openNewBanner}
          >
            {activeTab === 'coupons' ? 'New Coupon' : 'New Banner'}
          </Button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-200 pb-3">
        <button
          onClick={() => { setActiveTab('coupons'); setPage(1); }}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'coupons' ? 'bg-brand-600 text-white shadow-sm' : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
          }`}
        >
          🎟️ Platform Coupons
        </button>
        <button
          onClick={() => { setActiveTab('banners'); setPage(1); }}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'banners' ? 'bg-brand-600 text-white shadow-sm' : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
          }`}
        >
          🖼️ Storefront Banners
        </button>
      </div>

      {/* Coupons View */}
      {activeTab === 'coupons' && (
        <Card className="p-0 overflow-hidden">
          {couponLoading ? (
            <LoadingPage />
          ) : (Array.isArray(couponData) ? couponData : (couponData?.rows || couponData?.items || couponData?.data || [])).length === 0 ? (
            <EmptyState icon={TagIcon} title="No coupons found" description="Create promo codes for marketing campaigns." />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Coupon Code</th>
                    <th>Discount</th>
                    <th>Min. Order</th>
                    <th>Max. Cap</th>
                    <th>Valid Until</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(couponData) ? couponData : (couponData?.rows || couponData?.items || couponData?.data || [])).map((c) => (
                    <tr key={c.id}>
                      <td>
                        <span className="font-mono font-bold text-brand-700 bg-brand-50 px-2.5 py-1 rounded-lg text-xs border border-brand-200">
                          {c.code}
                        </span>
                      </td>
                      <td>
                        <span className="font-semibold text-xs text-surface-900">
                          {(c.type || c.discount_type) === 'percentage' ? `${c.value || c.discount_value}% OFF` : `₹${c.value || c.discount_value} FLAT`}
                        </span>
                      </td>
                      <td>
                        <span className="text-xs text-surface-600">₹{c.min_order_value || 0}</span>
                      </td>
                      <td>
                        <span className="text-xs text-surface-600">₹{c.max_discount || 'No cap'}</span>
                      </td>
                      <td>
                        <span className="text-xs text-surface-500">
                          {c.valid_until ? format(new Date(c.valid_until), 'dd MMM yyyy') : 'No expiry'}
                        </span>
                      </td>
                      <td>
                        <Badge variant={c.is_active !== false && c.is_active !== 0 ? 'green' : 'gray'}>
                          {c.is_active !== false && c.is_active !== 0 ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="ghost" size="sm" icon={PencilSquareIcon} onClick={() => openEditCoupon(c)} />
                          <Button variant="ghost" size="sm" icon={TrashIcon} onClick={() => onDeleteCoupon(c.id)} className="text-danger-600 hover:text-danger-700" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Banners View */}
      {activeTab === 'banners' && (
        <Card className="p-0 overflow-hidden">
          {bannerLoading ? (
            <LoadingPage />
          ) : (Array.isArray(bannerData) ? bannerData : (bannerData?.rows || bannerData?.data || [])).length === 0 ? (
            <EmptyState icon={PhotoIcon} title="No banners found" description="Create hero slides and promotional banners." />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Banner Preview</th>
                    <th>Title & Placement</th>
                    <th>Target Link</th>
                    <th>Status</th>
                    <th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(bannerData) ? bannerData : (bannerData?.rows || bannerData?.data || [])).map((b) => (
                    <tr key={b.id}>
                      <td>
                        <div className="h-14 w-28 rounded-lg overflow-hidden bg-surface-100 border border-surface-200">
                          {b.image_url ? (
                            <img src={b.image_url} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-surface-400">No image</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <p className="text-sm font-semibold text-surface-900">{b.title || 'Untitled Banner'}</p>
                        <p className="text-xs text-surface-400 capitalize">{b.position?.replace(/_/g, ' ') || 'Home Hero'}</p>
                      </td>
                      <td>
                        <p className="text-xs font-mono text-surface-600 truncate max-w-xs">{b.target_url || '—'}</p>
                      </td>
                      <td>
                        <Badge variant={b.is_active ? 'green' : 'gray'}>
                          {b.is_active ? 'Live' : 'Hidden'}
                        </Badge>
                      </td>
                      <td className="text-right">
                        <Button
                          variant={b.is_active ? 'secondary' : 'primary'}
                          size="sm"
                          onClick={() => onToggleBanner(b)}
                        >
                          {b.is_active ? 'Hide' : 'Activate'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Coupon Modal */}
      <Modal
        open={couponModalOpen}
        onClose={() => setCouponModalOpen(false)}
        title={editingCoupon ? 'Edit Coupon' : 'Create Platform Coupon'}
        size="md"
      >
        <form onSubmit={handleCouponSubmit(onSaveCoupon)} className="space-y-4">
          <Input label="Coupon Code" {...regCoupon('code', { required: true })} placeholder="e.g. FESTIVE20" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Discount Type" {...regCoupon('discount_type')}>
              <option value="percentage">Percentage (%)</option>
              <option value="flat">Flat Amount (₹)</option>
            </Select>
            <Input label="Discount Value" type="number" step="0.1" {...regCoupon('discount_value', { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min Order Value (₹)" type="number" {...regCoupon('min_order_value')} />
            <Input label="Max Discount Cap (₹)" type="number" {...regCoupon('max_discount')} />
          </div>
          <Input label="Valid Until" type="date" {...regCoupon('valid_until')} />

          <div className="pt-4 border-t border-surface-100 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setCouponModalOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit">Save Coupon</Button>
          </div>
        </form>
      </Modal>

      {/* Banner Modal */}
      <Modal
        open={bannerModalOpen}
        onClose={() => setBannerModalOpen(false)}
        title={editingBanner ? 'Edit Banner' : 'Create Promotional Banner'}
        size="md"
      >
        <form onSubmit={handleBannerSubmit(onSaveBanner)} className="space-y-4">
          <Input label="Banner Title" {...regBanner('title', { required: true })} placeholder="e.g. Summer Sale 50% Off" />
          <Input label="Banner Image URL" {...regBanner('image_url', { required: true })} placeholder="https://..." />
          <Input label="Target URL / Path" {...regBanner('target_url')} placeholder="/products?category=apparel" />
          <Select label="Placement Position" {...regBanner('position')}>
            <option value="home_hero">Homepage Main Hero Slider</option>
            <option value="category_top">Category Header Banner</option>
            <option value="promotional_popup">Promotional Modal / Popup</option>
          </Select>

          <div className="pt-4 border-t border-surface-100 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setBannerModalOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit">Publish Banner</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
